import Store from 'repatch';

import State from '../core/State';
import Account from '../entity/Account';
import User from '../entity/User';
import { disposeAcct } from '../reducer/acct';
import { disposeAgent } from '../reducer/agent';
import {
  disposeRedirectURL,
  disposeUser,
  finishAuthenticationWithPreviousSession,
  setSignedInUser,
  startAuthenticationWithPreviousSession,
} from '../reducer/authentication';
import { disposeBf } from '../reducer/bf';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import { disposeOrganization } from '../reducer/organization';
import { disposeUserPersonal } from '../reducer/userPersonal';
import ApiErrorHandler from '../service/ApiErrorHandler';
import IntercomService from '../service/IntercomService';
import MessageDispatcher from '../service/MessageDispatcher';
import AuthenticationWebApi from '../service/persistent/AuthenticationWebApi';
import UserWebApi from '../service/persistent/UserWebApi';
import ConfigurationChangingUsecase from './ConfigurationChangingUsecase';

class AuthenticationUsecase {
  private store: Store<State>;
  private authenticationWebApi: AuthenticationWebApi;
  private userWebApi: UserWebApi;
  private configurationChangingUsecase: ConfigurationChangingUsecase;
  private intercomService: IntercomService;
  private messageDispatcher: MessageDispatcher;
  private apiErrorHandler: ApiErrorHandler;

  constructor({
    store,
    authenticationWebApi,
    userWebApi,
    configurationChangingUsecase,
    intercomService,
    messageDispatcher,
    apiErrorHandler,
  }: {
    store: Store<State>;
    authenticationWebApi: AuthenticationWebApi;
    userWebApi: UserWebApi;
    configurationChangingUsecase: ConfigurationChangingUsecase;
    intercomService: IntercomService;
    messageDispatcher: MessageDispatcher;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.store = store;
    this.authenticationWebApi = authenticationWebApi;
    this.userWebApi = userWebApi;
    this.configurationChangingUsecase = configurationChangingUsecase;
    this.intercomService = intercomService;
    this.messageDispatcher = messageDispatcher;
    this.apiErrorHandler = apiErrorHandler;
  }

  public signInWithEmailAndPassword = async (email: string, password: string): Promise<string | void> => {
    this.store.dispatch(startSpinner());
    try {
      await this.authenticationWebApi.signIn({ email, password });
      await this.getCurrentUser();
    } catch (_err) {
      const err: any = _err;
      const errCode: string = err.response && err.response.data && err.response.data.code;
      if (errCode !== 'kaizen_id/otp_unauthenticated') {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject(errCode);
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public signInWithPreviousSession = async (): Promise<boolean> => {
    this.store.dispatch(startAuthenticationWithPreviousSession());
    try {
      await this.getCurrentUser();
      return true;
    } catch (_err) {
      const err: any = _err;
      const res = err.response;
      if (res && res.status == 403 && res.data && res.data.code == 'kaizen_id/unauthorized_access') {
        //ignore
      } else {
        this.apiErrorHandler.handle(err);
      }
    } finally {
      this.store.dispatch(finishAuthenticationWithPreviousSession());
    }
    return false;
  };

  public redirect = async (): Promise<void> => {
    const state = this.store.getState();
    const done = state.authentication.done || window.sessionStorage.getItem('done');

    if (state.fatalError || !state.authentication.signedInUser) {
      return;
    }

    if (done && done.length > 0) {
      const tdomain = window.location.hostname
        .split('.')
        .slice(-2)
        .join('.');

      if (tdomain == 'kaizenplatform.net' || tdomain == 'pbcd.net') {
        const matches = done.match(/^https?\:\/\/([^\/?#]+)(?:[\/?#]|$)/i); // eslint-disable-line no-useless-escape
        const domain = matches && matches[1];

        if (domain == null) {
          this.clearRedirectUrl();
          return;
        }

        const tdone = domain
          .split('.')
          .slice(-2)
          .join('.')
          .split(':')[0];

        if (tdone != tdomain) {
          this.clearRedirectUrl();
          return;
        }
      }

      window.sessionStorage.removeItem('done');
      window.location.href = done;
    }
  };

  public clearRedirectUrl = (): void => {
    window.sessionStorage.removeItem('done');
    this.store.dispatch(disposeRedirectURL());
  };

  public getCurrentUser = async (): Promise<void> => {
    const currentUser = await this.userWebApi.getCurrentUser();
    await this.configurationChangingUsecase.changeLanguage(currentUser.lang!);
    this.store.dispatch(setSignedInUser(currentUser));
    window.newrelic.setCustomAttribute('user_id', currentUser.id);
    this.identifyExternalServices(currentUser);
  };

  public signOut = (): void => {
    this.disposeSession().then(() => {
      this.disposeUser();
      this.disposeOrganization();
      this.disposeAcct();
      this.disposeBf();
      this.anonymizeExternalServices();
      this.disposeUserPersonal();
      this.disposeAgent();
    });
  };

  public requestPasswordReset = async (email: string): Promise<void> => {
    try {
      await this.authenticationWebApi.requestPasswordReset(email);
      this.messageDispatcher.notifyUnimportant('reset_password_request_form.reset_password_requested');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public resetPassword = async (token: string, newPassword: string): Promise<void> => {
    try {
      await this.authenticationWebApi.resetPassword(token, newPassword);
      this.messageDispatcher.notifyUnimportant('reset_password_form.password_reseted');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  private disposeSession = async (): Promise<void> => {
    try {
      await this.authenticationWebApi.signOut();
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  private disposeUser = () => {
    this.store.dispatch(disposeUser());
    window.newrelic.setCustomAttribute('user_id', 'signed_out');
  };

  private disposeOrganization = () => {
    this.store.dispatch(disposeOrganization());
  };

  private disposeAcct = () => {
    this.store.dispatch(disposeAcct());
  };

  private disposeBf = () => {
    this.store.dispatch(disposeBf());
  };

  private disposeUserPersonal = () => {
    this.store.dispatch(disposeUserPersonal());
  };

  private disposeAgent = () => {
    this.store.dispatch(disposeAgent());
  };

  public signInWithOtp = async (otp: string): Promise<string | void> => {
    this.store.dispatch(startSpinner());
    try {
      await this.authenticationWebApi.signInWithOtp({ otp });
      await this.getCurrentUser();
    } catch (_err) {
      const err: any = _err;
      const errCode: string = err.response && err.response.data && err.response.data.code;
      this.apiErrorHandler.handle(err);
      return Promise.reject(errCode);
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public recoverOtp = async (otpRecoveryCode: string): Promise<string | void> => {
    this.store.dispatch(startSpinner());
    try {
      await this.authenticationWebApi.recoverOtp({ otpRecoveryCode });
      await this.getCurrentUser();
    } catch (_err) {
      const err: any = _err;
      const errCode: string = err.response && err.response.data && err.response.data.code;
      this.apiErrorHandler.handle(err);
      return Promise.reject(errCode);
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public signUp = async ({
    email,
    username,
    password,
    // recaptcha,
    lang,
    invitation,
  }: {
    email?: string;
    username: string;
    password: string;
    // recaptcha?: string;
    lang: string;
    invitation?: string;
  }): Promise<void> => {
    this.store.dispatch(startSpinner());
    try {
      await this.authenticationWebApi.signUp({
        email,
        username,
        password,
        // recaptcha,
        lang: lang.toLowerCase(),
        invitation,
        via: this.via(),
      });
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public oauthAgreement = async (): Promise<void> => {
    try {
      await this.authenticationWebApi.oauthAgreement(this.via());
      await this.getCurrentUser();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  private via = () => {
    const done = window.sessionStorage.getItem('done');
    const via = JSON.parse(window.sessionStorage.getItem('via') || '{}');
    return JSON.stringify(Object.assign({}, via, { done: done }));
  };

  private identifyExternalServices = (user: User): void => {
    this.intercomService.identify(user);
  };

  private anonymizeExternalServices = (): void => {
    this.intercomService.anonymize();
  };

  public updateIntercomServiceByAccount = (user: User, account: Account): void => {
    this.intercomService.updateByAccount(user, account);
  };
}

export default AuthenticationUsecase;
